import {AbsenceState} from '../models/absence.model';

const STATUS_LABELS: Record<AbsenceState, string> = {
    NEU: 'Neu erfasst',
    JOB_ERSTELLT: 'Abwesenheit wird angelegt',
    JOB_ERFOLGREICH: 'Abwesenheit angelegt',
    JOB_ABGESCHLOSS: 'Abwesenheit bestätigt',
    EAU_ERSTELLT: 'Krankmeldung erstellt',
    EAU_EINGEREICHT: 'Krankmeldung eingereicht',
    EAU_GEHOLT: 'Krankmeldung beantwortet',
    CANCEL_ABWESENHEIT: 'Abwesenheit wird gelöscht',
    // EAU_CANCEL ist - anders als der Name suggeriert - kein Endzustand: die Krankmeldung
    // wurde bei der Krankenkasse eingereicht und muss dort noch storniert werden (siehe
    // ProcessCancelEau im Backend). Es ist das Spiegelbild von EAU_ERSTELLT/EAU_EINGEREICHT,
    // nicht von GELOESCHT - daher wie diese als "ausstehend" und nicht als "erledigt" labeln.
    EAU_CANCEL: 'Krankmeldung wird storniert',
    GELOESCHT: 'Abwesenheit gelöscht',
    SYNCED: 'Synchronisiert',
    FEHLERHAFT: 'Fehlerhaft',
};

export function getAbsenceStatusText(status: AbsenceState): string {
    return STATUS_LABELS[status] ?? status;
}

export function isCancelledAbsence(status: AbsenceState): boolean {
    // EAU_CANCEL bewusst ausgeschlossen: die Stornierung steht dort noch aus (siehe Kommentar
    // bei STATUS_LABELS.EAU_CANCEL) - die Zeile zeigt in diesem Status einen aktiven
    // "Jetzt stornieren"-Button und darf daher nicht wie ein erledigter/gestrichener Eintrag
    // aussehen.
    return status === 'CANCEL_ABWESENHEIT' || status === 'GELOESCHT';
}

export function isErrorAbsence(status: AbsenceState): boolean {
    return status === 'FEHLERHAFT';
}

const STATUS_CLASSES: Record<AbsenceState, string> = {
    NEU: 'status-new',
    JOB_ERSTELLT: 'status-pending',
    JOB_ERFOLGREICH: 'status-pending',
    JOB_ABGESCHLOSS: 'status-pending',
    EAU_ERSTELLT: 'status-pending',
    EAU_EINGEREICHT: 'status-pending',
    SYNCED: 'status-pending',
    EAU_GEHOLT: 'status-success',
    CANCEL_ABWESENHEIT: 'status-cancelled',
    // pending, actionable state (siehe STATUS_LABELS.EAU_CANCEL) - wie EAU_ERSTELLT/EAU_EINGEREICHT.
    EAU_CANCEL: 'status-pending',
    GELOESCHT: 'status-cancelled',
    FEHLERHAFT: 'status-error',
};

export function getAbsenceStatusClass(status: AbsenceState): string {
    return STATUS_CLASSES[status] ?? 'status-cancelled';
}

export type AbsenceErrorGrund =
    'JOB_ERSTELLUNG_FEHLGESCHLAGEN' | 'JOB_FEHLER' | 'JOB_TIMEOUT' | 'PERSISTENZ_ABWEICHUNG';

const ERROR_GRUND_LABELS: Record<AbsenceErrorGrund, string> = {
    JOB_ERSTELLUNG_FEHLGESCHLAGEN: 'Job-Erstellung bei DATEV fehlgeschlagen',
    JOB_FEHLER: 'Übertragung bei DATEV fehlgeschlagen',
    JOB_TIMEOUT: 'Zeitüberschreitung bei der Übertragung an DATEV',
    PERSISTENZ_ABWEICHUNG: 'Datenabgleich mit DATEV fehlgeschlagen',
};

export function getErrorGrundText(errorGrund?: string | null): string {
    if (!errorGrund) {
        return '';
    }
    return ERROR_GRUND_LABELS[errorGrund as AbsenceErrorGrund] ?? errorGrund;
}

export interface AbsenceErrorDetail {
    headline: string;
    detail: string | null;
}

// error_details ist Freitext-JSON vom Backend, dessen Form vom error_grund abhängt und nicht
// immer gesetzt ist - daher defensiv parsen statt dem Feld zu vertrauen.
export function getAbsenceErrorDetail(leave: { error_grund?: string | null; error_details?: string | null }): AbsenceErrorDetail {
    const headline = getErrorGrundText(leave.error_grund);

    if (!leave.error_details) {
        return {headline, detail: null};
    }

    let parsed: any;
    try {
        parsed = JSON.parse(leave.error_details);
    } catch {
        return {headline, detail: null};
    }

    // PERSISTENZ_ABWEICHUNG liefert bereits einen fertigen deutschen Diff-Satz (siehe
    // hrexchange-job.service.ts im Backend) - der ist die Hauptmotivation für diese Funktion
    // und wird anderen Feldern vorgezogen.
    if (leave.error_grund === 'PERSISTENZ_ABWEICHUNG' && typeof parsed?.mismatch === 'string') {
        return {headline, detail: parsed.mismatch};
    }

    if (typeof parsed?.datev_error_message === 'string') {
        return {headline, detail: parsed.datev_error_message};
    }

    return {headline, detail: null};
}
